import { v4 as uuid } from 'uuid';

const STORAGE_BUCKET = 'playmix-assets';

/**
 * Checks if a string is a URL
 * @param str String to check
 * @returns Boolean indicating if the string is a URL
 */
export const isUrl = (str: string): boolean => {
  try {
    return str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/');
  } catch (e) {
    return false;
  }
};

/**
 * Converts a URL to a base64 string
 * @param url URL to convert
 * @returns Promise resolving to base64 string
 */
export const urlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting URL to base64:', error);
    return url; // Return original URL if conversion fails
  }
};

/**
 * Efficiently converts base64 data to a Blob
 * @param base64Data Base64 encoded data
 * @returns Blob object
 */
const base64ToBlob = (base64Data: string): Blob => {
    // Detect MIME type from a data URL if present; default to PNG
    const mimeMatch = base64Data.startsWith('data:')
      ? base64Data.match(/^data:([^;]+);base64,/)
      : null;
    const contentType = mimeMatch && mimeMatch[1] ? mimeMatch[1] : 'image/png';

    // Extract the base64 payload
    const data = base64Data.includes('base64,')
      ? base64Data.split('base64,')[1]
      : base64Data;

    // Convert base64 to binary efficiently
    const byteString = atob(data);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: contentType });
};

/**
 * Converts a Blob to a base64 data URL
 * @param blob Blob to convert
 * @returns Promise resolving to base64 data URL
 */
export const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Ensures image data is in base64 format, converting URLs if necessary
 * @param imageData Image data (URL or base64)
 * @returns Promise resolving to base64 image data
 */
export const ensureBase64 = async (imageData: string): Promise<string> => {
  if (!imageData) return '';
  
  // If it's already a data URL or base64 string, return as is
  if (imageData.startsWith('data:')) return imageData;
  
  // If it's a URL, convert to base64
  if (isUrl(imageData)) {
    return await urlToBase64(imageData);
  }
  
  // Assume it's already base64 data without the data URL prefix
  return imageData;
};

/**
 * Uploads a Blob to Supabase storage
 * @param supabase The Supabase client instance
 * @param fileBlob The file data as a Blob
 * @param prefix Optional prefix for the file path
 * @param folder The storage folder (e.g., 'animation-assets')
 * @param contentType The MIME type of the file (e.g., 'video/mp4')
 * @returns The public URL of the uploaded file
 */
export const uploadBlobToStorage = async (
  supabase, 
  fileBlob: Blob, 
  prefix: string = '', 
  folder: string,
  contentType: string
): Promise<string> => {
    if (!fileBlob) {
      console.log('No file blob provided for upload');
      return '';
    }
  
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No active session, cannot upload to storage.');
        throw new Error('Authentication required for file upload.');
      }
      
      // Always generate a new file name
      const fileExtension = contentType.split('/')[1] || 'mp4';
      const fileName = `${prefix ? prefix + '-' : ''}${uuid()}.${fileExtension}`;
      const filePath = `${folder}/${fileName}`;
      
      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, fileBlob, {
          contentType: contentType,
          upsert: true // Enable upsert to update existing files
        });
      
      if (uploadError) {
        console.error('Error uploading file to storage:', uploadError);
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }
      
      // Get the public URL for the uploaded file
      const { data: publicUrlData } = supabase
        .storage
        .from(STORAGE_BUCKET)
        .getPublicUrl(filePath);
      
      return publicUrlData.publicUrl;

    } catch (error) {
      console.error('Error in uploadBlobToStorage:', error);
      throw error;
    }
};

/**
 * Gets the public URL for an image in Supabase storage
 * @param supabase The Supabase client instance
 * @param storagePath The storage path of the image
 * @returns The public URL of the image
 */
export const getPublicUrl = (supabase, storagePath: string): string => {
  if (!storagePath) return '';
  
  // If it's already a URL or data URL, return as is
  if (storagePath.startsWith('data:') || storagePath.startsWith('http://') || storagePath.startsWith('https://')) {
    return storagePath;
  }
  
  const { data } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(storagePath);
  
  return data.publicUrl;
};

/**
 * Uploads an image to Supabase storage
 * @param imageData Base64 encoded image data
 * @param prefix Optional prefix for the file path
 * @returns The storage path of the uploaded image
 */
export const uploadImageToStorage = async (supabase, imageData: string, prefix: string = '', folder: string): Promise<string> => {
    if (!imageData) {
      // console.log('No image data provided for upload');
      return '';
    }
  
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        // console.log('No active session, will use direct image data instead of storage');
        return imageData;
      }
  
      // Ensure the image data is in base64 format
      const base64Data = await ensureBase64(imageData);
      
      // Handle data URL format
      let blob: Blob;
      if (base64Data.startsWith('data:')) {
        // Convert data URL directly to blob
        blob = base64ToBlob(base64Data);
      } else {
        // Handle base64 string without data URL prefix
        blob = base64ToBlob(base64Data);
      }

      // Always generate a new file name to avoid requiring list permissions
      const fileName = `${prefix ? prefix + '-' : ''}${uuid()}.png`;
      const filePath = `${folder}/${fileName}`;
      
      // Upload to Supabase storage with upsert enabled
      const { error: uploadError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .upload(filePath, blob, {
          contentType: 'image/png',
          upsert: true // Enable upsert to update existing files
        });
      
      if (uploadError) {
        console.error('Error uploading image to storage:', uploadError);
        // Fall back to returning the original image data
        return imageData;
      }
      
      // Return the storage path
      return filePath;
    } catch (error) {
      console.error('Error in uploadImage:', error);
      // Fall back to returning the original image data
      return imageData;
    }
};